import {Reel} from 'com/gongxigames/math/core/game/Reel';
import {Pay} from 'com/gongxigames/math/core/game/Pay';
import {inject} from 'inversify';
import {CoreDI, LockManager} from '@gongxigames/framework/core';
import {ReelsCascadingStates, ReelsCascadingStatesMap, ReelsCascadingView} from '@gongxigames/framework/slot/modules/reelsCascading';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {ReelsEvents, ReelsModule, ReelsView, ReelsViewEvents} from '@gongxigames/framework/slot/modules/reels';
import {GameConfig} from '@gongxigames/framework/slot/types';
import {FreeSpinsModule} from '@gongxigames/framework/slot/modules/freespins';
import {BuyBonusPopUpEvents} from '@gongxigames/framework/slot/modules/buyBonus';
import {WinPresentationEvents} from '@gongxigames/framework/slot/modules/winpresentation';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow';
import {SpinButtonEvents} from '@gongxigames/framework/slot/modules/spinbutton';
import {KeypadControlsEvents} from '@gongxigames/framework/slot/modules/keypadControls';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic';
import {GenericPopupEvents} from '@gongxigames/framework/slot/modules/genericPopup';
import {ReasonsForDisablingSpin} from '@gongxigames/framework/slot/events';
import {ReelConstants} from '@gongxigames/framework/slot';
import {ReelsCascadingConfig} from '@gongxigames/framework/slot/types/ReelsCascadingConfig';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';

export class ReelsCascadingModule extends Module<ReelsCascadingView> {
    public static readonly NAME: symbol = ReelsModule.NAME;
    private readonly CAN_SPIN_BY_SPIN_BUTTON = 'canSpinBySpinButton';

    @inject(CoreDI.GameConfig)
    private _gameConfig!: GameConfig;

    protected _reels!: Reel[];
    protected _isTurboSpin = false;
    protected lockManager: LockManager = new LockManager();

    protected _canSpinBySpinButton = true;

    public init(): void {
        super.init();

        this._reels = this.logic.getMainGameReelSet().getReels();
        this.lockManager.add('canSpinBySpinButton', this.canSpinBySpinButtonChanged.bind(this));
        this.gotoState(ReelsCascadingStates.INIT_STATE);
    }

    public resetParameters(): void {
        this.resetTurboSpinMode();
    }

    protected resetTurboSpinMode(): void {
        this._isTurboSpin = this.detectQuickSpin();
        this.view.isTurboSpin = this._isTurboSpin;
    }

    protected detectQuickSpin(): boolean {
        return this.settings.get('quickSpin') && this.logic.nextAction !== Action.FREESPIN && this.logic.nextAction !== Action.RESPIN;
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.onCurrentStateHandle('Autoplay.START_SPIN');
        this.onCurrentStateHandle('General.BuyFeature.BUY');
        this.onCurrentStateHandle(FreeSpinsModule.FREESPIN_START_SPIN);
        this.onCurrentStateHandle(WinPresentationEvents.PAY_ANIMATION_STARTED);
        this.onCurrentStateHandle(BuyBonusPopUpEvents.BUY_BUTTON_CLICKED);
        this.onCurrentStateHandle(FlowModuleEvents.RESPIN_WIN);
        this.onCurrentStateHandle(WinPresentationEvents.INITIAL_ANIMATION_STARTED);
        this.onCurrentStateHandle(GenericPopupEvents.FADE_OUT_STARTED);
        this.onCurrentStateHandle(GenericPopupEvents.INTRO_STARTED);
        this.onCurrentStateHandle(LogicEvents.SPIN_RESPONSE);
        this.onCurrentStateHandle(LogicEvents.FREE_SPIN_RESPONSE);
        this.onCurrentStateHandle(LogicEvents.RESPIN_RESPONSE);
        this.onCurrentStateHandle(SpinButtonEvents.SPIN_BUTTON_PRESSED);
        this.onCurrentStateHandle(FlowModuleEvents.FREESPINS_RESTORED);
        this.onCurrentStateHandle(FlowModuleEvents.START_COLLAPSING);

        this.on(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, this.onInitialAnimationFinished);
        this.on(KeypadControlsEvents.TOTAL_BET_CHANGED, this.onBetChanged);
        this.on(FlowModuleEvents.PICK_GAME_RESTORED, this.onPickGameRestored);
        this.on(ReelsEvents.ENABLE_SPIN_BY_SPIN_BUTTON, this.enableSpin);
        this.on(ReelsEvents.DISABLE_SPIN_BY_SPIN_BUTTON, this.disableSpin);
        this.on('General.Slot.SPIN.DISABLE', this.disableSpin);
        this.on('General.Slot.SPIN.ENABLE', this.enableSpin);
        this.on('General.WinPresentation.PAYS_ANIMATION_STOPPED', this.onPaysAnimationStopped);

        this.onViewCurrentStateHandle(ReelsViewEvents.ALL_REELS_STOPPED);
    }

    protected setupViews(): void {
        this.setView(ReelsView.NAME);
    }

    protected setupStates(): void {
        this.addStates(ReelsCascadingStatesMap);
    }

    protected onInitialAnimationFinished(id: symbol): void {
        this.view.setAllSymbolsVisible(true);
        this.currentState.handle(id);
    }

    protected onPaysAnimationStopped(): void {
        this.view.setAllSymbolsVisible(true);
    }

    protected onPickGameRestored(): void {
        const {spinData} = this.logic;

        this.restoreSymbols(spinData.reelPositions);
    }

    protected onBetChanged(): void {
        this.view.setAllSymbolsVisible(true);
    }

    protected enableSpin(eventID: string | symbol, reason: ReasonsForDisablingSpin = 'default'): void {
        this.lockManager.unlock(this.CAN_SPIN_BY_SPIN_BUTTON, reason);
    }

    protected disableSpin(eventID: string | symbol, reason: ReasonsForDisablingSpin = 'default'): void {
        this.lockManager.lock(this.CAN_SPIN_BY_SPIN_BUTTON, reason);
    }

    protected canSpinBySpinButtonChanged(lock: boolean): void {
        this._canSpinBySpinButton = !lock;
    }

    public restoreSymbols(stopPositions: number[]): void {
        const reelsPositions = ReelConstants.REELS;
        const reels = this.reels;

        this.view.removeAllSymbols();
        this.view.setupReelsLayers();

        reels.forEach((reel, reelIndex) => {
            const strip = reel.getSymbols().concat(reel.getSymbols());
            const startIndex = stopPositions[reelIndex];
            const symsPositions = reelsPositions[reelIndex].symsPositions;

            for (let i = startIndex; i < symsPositions.length + startIndex; i++) {
                const symbolID = strip[i];

                this.view.addSymbol({symbolID, coordinates: {x: reelIndex, y: i - startIndex}});
            }
        });

        this.view.forceUpdateOrientation();
    }

    public hideByPayPosition(pay: Pay): void {
        for (const pos of pay.positions) {
            this.view.setSymbolVisible({x: pos.reel, y: pos.row}, false);
        }
    };

    public get reels(): Reel[] {
        return this._reels;
    }

    public get canSpinBySpinButton(): boolean {
        return this._canSpinBySpinButton;
    }

    public getGameConfig<T extends GameConfig>(): T {
        return this._gameConfig as T;
    }

    public get initialStopPositions(): number[] {
        return Array.from(
            { length: this.reels.length },
            () => this.reelsCascadingConfig.initialStopPosition
        );
    }

    public get reelsCascadingConfig(): ReelsCascadingConfig {
        if (this._gameConfig.reelsCascadingConfig === undefined) {
            throw new Error('ReelsCascadingConfig is not defined. Add it in your GameConfig');
        }

        return this._gameConfig.reelsCascadingConfig!;
    }

    public isTurboSpin(): boolean {
        return this._isTurboSpin;
    }

    public isAnyCollapseOnSpin(): boolean {
        return true;
    }
}
